import { Box, Button, FormControl, Grid } from "@mui/material";
import { Field, Form, Formik } from "formik";
import React, { useMemo } from "react";
import styles from "../../prepare-invoice/styles/prepareInvoice.module.scss";
import { IFieldForm } from "pages/read-documents/model/IFieldForm";
import { fieldsFormCreateCostCenter } from "../helpers/fieldsFormCostCenter";
import * as Yup from "yup";

interface Props {
  onClose: () => void;
  initialValues?: Record<string, any>;
  onSubmit?: (values: Record<string, string>) => void;
  stateOptions?: any[];
}

export const FormCostCenter = ({
  onClose,
  initialValues,
  onSubmit,
  stateOptions,
}: Props) => {
  const dataFields = useMemo(
    () => fieldsFormCreateCostCenter(stateOptions),
    [stateOptions],
  );

  const formInitialValues = useMemo(() => {
    return dataFields.reduce<Record<string, string>>((acc, field: any) => {
      const providedValue = initialValues?.[field.name];
      acc[field.name] =
        providedValue !== undefined && providedValue !== null
          ? String(providedValue)
          : "";
      return acc;
    }, {});
  }, [dataFields, initialValues]);

  const validationSchema = useMemo(() => {
    const shape: Record<string, Yup.AnySchema> = {};

    dataFields.forEach((field: any) => {
      let schema = Yup.string();

      if (field?.required) {
        schema = schema.required("Campo requerido");
      }

      if (field?.maxLength) {
        schema = schema.max(field.maxLength);
      }

      shape[field.name] = schema;
    });

    return Yup.object().shape(shape);
  }, [dataFields]);

  return (
    <div className={styles.container}>
      <Formik
        initialValues={formInitialValues}
        onSubmit={(values) => {
          onSubmit?.(values);
        }}
        enableReinitialize={true}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={validationSchema}
      >
        {(formik) => (
          <Form
            className={styles.formPrepareInvoice}
            data-testid="form-cost-center"
          >
            <Grid container>
              {dataFields.map((field: IFieldForm, i: number) => (
                <Grid item xs={12} md={6} lg={4} key={i}>
                  <FormControl fullWidth className={styles.itemForm}>
                    <label
                      htmlFor={field.id}
                      className={`${(field as any).required ? "text-required" : "label-form-required"}`}
                    >
                      {field.label}
                    </label>
                    {field.type === "select" ? (
                      <Field
                        id={field.id}
                        as="select"
                        name={field.name}
                        className={
                          (field as any).required &&
                          Object.keys(formik.errors).includes(field.name) &&
                          formik.touched[field.name]
                            ? styles.errorCampo
                            : ""
                        }
                        disabled={!field.isEnable}
                        data-testid={`input_${field.name}`}
                        onBlur={() => formik.setFieldTouched(field.name)}
                      >
                        <option value="">{field.placeholder}</option>
                        {field.options?.map((option: any) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </Field>
                    ) : (
                      <Field
                        id={field.id}
                        placeholder={field.placeholder}
                        type={field.type}
                        variant="outlined"
                        name={field.name}
                        maxLength={(field as any).maxLength}
                        className={
                          (field as any).required &&
                          Object.keys(formik.errors).includes(field.name) &&
                          formik.touched[field.name]
                            ? styles.errorCampo
                            : ""
                        }
                        disabled={!field.isEnable}
                        autoComplete="off"
                        data-testid={`input_${field.name}`}
                        onBlur={() => formik.setFieldTouched(field.name)}
                      />
                    )}
                    <Box display="flex" justifyContent="space-between">
                      <span className={styles.spanError}>
                        {(field as any).required &&
                          Object.keys(formik.errors).includes(field.name) &&
                          formik.touched[field.name] &&
                          (formik.errors as any)[field.name]}
                        {!(field as any).required &&
                          formik.touched[field.name] &&
                          (formik.errors as any)[field.name]}
                      </span>
                      {(field as any).maxLength && field.type !== "select" ? (
                        <span
                          className={styles.spanCounter}
                          data-testid={`error_field_${field.name}`}
                        >
                          {formik?.values?.[field.name]?.toString().length || 0}
                          /{(field as any).maxLength}
                        </span>
                      ) : null}
                    </Box>
                  </FormControl>
                </Grid>
              ))}

              <Grid item xs={12}>
                <div className={styles.containerButtons}>
                  <Button
                    className={styles.buttonCancel}
                    onClick={() => {
                      onClose();
                      formik.resetForm();
                    }}
                    data-testid="button-cancel"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    data-testid="button-submit"
                    variant="contained"
                    className={styles.buttonSubmit}
                  >
                    Guardar
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};
